import { IconFiSearch } from "./Icons";
import { useState, useCallback } from "react";
import {
    Box,
    Button,
    ButtonGroup,
    Flex,
    Grid,
    Heading,
    Select,
    Text,
    useDisclosure,
    SlideFade,
    useBreakpointValue,
} from "@chakra-ui/react";

type TphMode = "simple" | "trace";

interface FilterState {
    jenis_laporan?: string;
    tahun?: string;
    bulan?: string;
    estate?: string;
    tphMode?: TphMode;
}

interface FilterProduksiProps {
    filters: FilterState;
    onFilterChange: (filters: FilterState) => void;
    onClearFilters: () => void;
    onApplyFilters: () => void;
}

const FilterProduksi = ({
    filters,
    onFilterChange,
    onClearFilters,
    onApplyFilters,
}: FilterProduksiProps) => {
    const [localFilters, setLocalFilters] = useState<FilterState>(filters);

    // Get year options (3 years back from current year)
    const getYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = 0; i < 3; i++) {
            years.push(currentYear - i);
        }
        return years.sort((a, b) => b - a);
    };

    // Month options
    const monthOptions = Array.from({ length: 12 }, (_, i) => ({
        value: (i + 1).toString().padStart(2, '0'),
        label: new Date(2000, i).toLocaleString('id-ID', { month: 'long' })
    }));

    const handleChange = useCallback(
        (key: keyof FilterState, value: string | boolean | undefined) => {
            let newFilters = { ...localFilters, [key]: value };

            // Reset TPH mode when jenis_laporan is not eligible
            if (key === "jenis_laporan" && value !== "aktual_vs_target_mtd") {
                newFilters.tphMode = undefined;
            }

            setLocalFilters(newFilters);
            onFilterChange(newFilters);
        },
        [localFilters, onFilterChange]
    );
    const footerGap = useBreakpointValue({ base: 2, md: 3 });

    const { isOpen: isAdvancedOpen, onToggle: onAdvancedToggle } = useDisclosure({ defaultIsOpen: true });

    const handleClear = useCallback(() => {
        setLocalFilters({});
        onClearFilters();
    }, [onClearFilters]);

    const handleApply = useCallback(() => {
        onApplyFilters();
    }, [onApplyFilters]);

    const handleTphModeChange = useCallback(
        (mode: TphMode) => {
            if (localFilters.jenis_laporan !== "aktual_vs_target_mtd") return;

            const updatedFilters = { ...localFilters, tphMode: mode };
            setLocalFilters(updatedFilters);
            onFilterChange(updatedFilters);
        },
        [localFilters, onFilterChange]
    );

    const isTphModeEnabled = localFilters.jenis_laporan === "aktual_vs_target_mtd";

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="100vh"
            bg="white"
        >
            {/* Header Section - Fixed */}
            <Flex
                as="header"
                position="sticky"
                top={0}
                bg="white"
                zIndex={2}
                px={useBreakpointValue({ base: 3, md: 4 })}
                py={useBreakpointValue({ base: 2, md: 0 })}
                align="center"
                minHeight={useBreakpointValue({ base: "60px", md: "72px" })}
            >
                <Heading size={useBreakpointValue({ base: "md", md: "md" })}>
                    Produksi Aktual vs Target
                </Heading>

            </Flex>

            {/* Scrollable Content */}
            <Box
                flex={1}
                overflowY="auto"
                position="relative"
            >
                <SlideFade in={isAdvancedOpen}>
                    <Grid templateColumns="1fr" gap={4} p="5"> {/* Tambahkan padding bottom untuk footer */}
                        <Box width="full">
                            <Text mb={2}>Jenis Laporan</Text>
                            <Select
                                value={localFilters.jenis_laporan ?? ""}
                                onChange={(e) => handleChange("jenis_laporan", e.target.value)}
                                placeholder="Pilih Jenis Laporan"
                            >
                                <option value="aktual_vs_target_ytd">Yield YTD</option>
                                <option value="aktual_vs_target_mtd">Yield MTD</option>
                                <option value="rotasi_panen">Rotasi Panen</option>
                                <option value="brondolan_mtd">% Brondolan MTD</option>
                                {/* <option value="yield_ytd">% Yield YTD</option> */}
                                <option value="bjr_ytd">BJR YTD</option>
                                <option value="sph">% SPH</option>
                            </Select>
                        </Box>
                        <Box width="full">
                            <Text mb={2}>Tahun</Text>
                            <Select
                                value={localFilters.tahun ?? ""}
                                onChange={(e) => handleChange("tahun", e.target.value)}
                                placeholder="Pilih Tahun"
                            >
                                {getYearOptions().map(year => (
                                    <option key={year} value={year.toString()}>
                                        {year}
                                    </option>
                                ))}
                            </Select>
                        </Box>
                        <Box width="full">
                            <Text mb={2}>Bulan</Text>
                            <Select
                                value={localFilters.bulan ?? ""}
                                onChange={(e) => handleChange("bulan", e.target.value)}
                                placeholder="Pilih Bulan"
                            >
                                {monthOptions.map(month => (
                                    <option key={month.value} value={month.value}>
                                        {month.label}
                                    </option>
                                ))}
                            </Select>
                        </Box>
                        <Box width="full">
                            <Text mb={2}>Estate</Text>
                            <Select
                                value={localFilters.estate ?? ""}
                                onChange={(e) => handleChange("estate", e.target.value)}
                                placeholder="Semua Estate"
                            >
                                <option value="ESTATE LEMATANG MAKMUR">ESTATE LEMATANG MAKMUR</option>
                                <option value="ESTATE MUSI MAKMUR">ESTATE MUSI MAKMUR</option>
                                <option value="PLASMA ABAB MAKMUR 1">PLASMA ABAB MAKMUR 1</option>
                                <option value="PLASMA ABAB MAKMUR 2">PLASMA ABAB MAKMUR 2</option>
                            </Select>
                        </Box>
                        
                        {/* TPH Points Mode - Only available for Yield MTD */}
                        <Box width="full" pt={2}>
                            <Text mb={2} color={isTphModeEnabled ? "gray.800" : "gray.400"}>Titik TPH</Text>
                            <ButtonGroup isAttached variant="outline" size="sm" w="full">
                                <Button
                                    w="full"
                                    colorScheme={localFilters.tphMode === "simple" ? "green" : undefined}
                                    onClick={() => handleTphModeChange("simple")}
                                    isDisabled={!isTphModeEnabled}
                                    variant={localFilters.tphMode === "simple" ? "solid" : "outline"}
                                >
                                    Tampilkan Titik TPH
                                </Button>
                                <Button
                                    w="full"
                                    colorScheme={localFilters.tphMode === "trace" ? "green" : undefined}
                                    onClick={() => handleTphModeChange("trace")}
                                    isDisabled={!isTphModeEnabled}
                                    variant={localFilters.tphMode === "trace" ? "solid" : "outline"}
                                >
                                    Trace SPB & BKM
                                </Button>
                            </ButtonGroup>
                            {!isTphModeEnabled && (
                                <Text fontSize="xs" color="gray.400" mt={1}>
                                    Hanya tersedia untuk Yield MTD
                                </Text>
                            )}
                        </Box>
                    </Grid>
                </SlideFade>
            </Box>
            {/* Fixed Footer */}
            {/* <Box
            // top="100px"
            bottom={{ base: "60px", md: "0" }}
            left={0}
            right={0}
            p={4}
            bg="white"
            position="absolute"
            height="100px"
          > */}
            <Flex
                // as="footer"
                position="sticky"
                // bottom={footerBottom}
                // sx={{
                //   "@media (max-width:786px)": { bottom: "60px" },
                //   "@media (max-width:350px)": { bottom: "0" },
                //   "@media (min-width:600px)": { bottom: "0" },
                // }}
                bg="white"
                zIndex={3}
                // px={footerPaddingX}
                // py={footerPaddingY}
                padding="16px"
                // align="center"
                justify="flex-end"
                gap={footerGap}
                minHeight="240px"
            >
                <Button
                    colorScheme="red"
                    variant="outline"
                    onClick={handleClear}
                    size="sm"
                >
                    Hapus Filter
                </Button>
                <Button
                    colorScheme="green"
                    size="sm"
                    leftIcon={<IconFiSearch />}
                    onClick={handleApply}
                >
                    Terapkan Filter
                </Button>
            </Flex>
            {/* </Box> */}
        </Box>
    );
};

export default FilterProduksi;
